/**
 * PricingRuleDiscountWidget — Discount configuration based on rule type.
 *
 * Adapts its fields based on which type was selected in the type picker:
 *   - Price Discount / Coupon: rate_or_discount, rate/discount_percentage/discount_amount
 *   - Product Discount: free_item, free_qty, same_item
 *
 * Hidden until type is selected (progressive disclosure).
 * Follows StockEntryWarehouseWidget pattern.
 */

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";
import { CARD } from "@/config/design-tokens";

const i18n: Record<string, { en: string; th: string }> = {
  discount_type: { en: "Discount Type", th: "ประเภทส่วนลด" },
  rate: { en: "Selling Rate", th: "ราคาขาย" },
  discount_pct: { en: "Discount %", th: "ส่วนลด %" },
  discount_amt: { en: "Discount Amount", th: "จำนวนส่วนลด" },
  max_discount: { en: "Max Discount Amount", th: "ส่วนลดสูงสุด" },
  apply_on_label: { en: "Apply Discount On", th: "คิดส่วนลดจาก" },
  price_list: { en: "For Price List", th: "สำหรับรายการราคา" },
  free_item: { en: "Free Item", th: "สินค้าแถม" },
  free_qty: { en: "Free Qty", th: "จำนวนแถม" },
  free_rate: { en: "Free Item Rate", th: "ราคาสินค้าแถม" },
  same_item_label: { en: "Same Item", th: "สินค้าเดียวกัน" },
  same_item_hint: { en: "Give the triggering item as free item", th: "แถมสินค้าตัวเดียวกับที่ซื้อ" },
  select_item: { en: "Select item...", th: "เลือกสินค้า..." },
  title: { en: "Title", th: "ชื่อ" },
  title_placeholder: { en: "e.g. 10% off all items", th: "เช่น ลด 10% ทุกสินค้า" },
};

type DiscountMode = "price" | "product" | null;

function getDiscountMode(formData: Record<string, unknown>): DiscountMode {
  const type = formData.price_or_product_discount;
  if (type === "Price") return "price";
  if (type === "Product") return "product";
  return null;
}

export function PricingRuleDiscountWidget({
  formData,
  onFieldChange,
  disabled,
  isSaving,
  viewMode,
}: ExperienceWidgetProps) {
  // On the detail (experience) page the title is edited elsewhere — hide the
  // duplicate title field this widget shows in the create wizard.
  const showTitle = viewMode !== "experience";
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };

  const mode = useMemo(() => getDiscountMode(formData), [formData.price_or_product_discount]);

  // Don't render until type is selected
  if (!mode) return null;

  const rateOrDiscount = (formData.rate_or_discount as string) || "";
  const isInputDisabled = disabled || isSaving;

  return (
    <div>
      <SectionHeader
        label={getSectionTitleTranslation(
          mode === "product" ? "Free Item" : "Discount",
          language
        )}
      />
      <div className={cn(CARD, "p-6 space-y-5")}>
        {/* Title field — create wizard only (edited elsewhere on the detail page) */}
        {showTitle && (
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">{t("title")}</Label>
            <Input
              value={(formData.title as string) || ""}
              onChange={(e) => onFieldChange("title", e.target.value)}
              placeholder={t("title_placeholder")}
              disabled={isInputDisabled}
            />
          </div>
        )}

        {mode === "price" && (
          <>
            {/* Rate or Discount selector */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">{t("discount_type")}</Label>
              <Select
                value={rateOrDiscount}
                onValueChange={(val) => onFieldChange("rate_or_discount", val)}
                disabled={isInputDisabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("discount_type")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Rate">{t("rate")}</SelectItem>
                  <SelectItem value="Discount Percentage">{t("discount_pct")}</SelectItem>
                  <SelectItem value="Discount Amount">{t("discount_amt")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Conditional value field */}
            {rateOrDiscount === "Rate" && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t("rate")}</Label>
                <Input
                  type="number"
                  value={(formData.rate as number) ?? ""}
                  onChange={(e) => onFieldChange("rate", parseFloat(e.target.value) || 0)}
                  disabled={isInputDisabled}
                />
              </div>
            )}

            {rateOrDiscount === "Discount Percentage" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">{t("discount_pct")}</Label>
                  <Input
                    type="number"
                    value={(formData.discount_percentage as number) ?? ""}
                    onChange={(e) =>
                      onFieldChange("discount_percentage", parseFloat(e.target.value) || 0)
                    }
                    disabled={isInputDisabled}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">{t("max_discount")}</Label>
                  <Input
                    type="number"
                    value={(formData.max_discount_amount as number) ?? ""}
                    onChange={(e) =>
                      onFieldChange("max_discount_amount", parseFloat(e.target.value) || 0)
                    }
                    disabled={isInputDisabled}
                  />
                </div>
              </div>
            )}

            {rateOrDiscount === "Discount Amount" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">{t("discount_amt")}</Label>
                  <Input
                    type="number"
                    value={(formData.discount_amount as number) ?? ""}
                    onChange={(e) =>
                      onFieldChange("discount_amount", parseFloat(e.target.value) || 0)
                    }
                    disabled={isInputDisabled}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">{t("max_discount")}</Label>
                  <Input
                    type="number"
                    value={(formData.max_discount_amount as number) ?? ""}
                    onChange={(e) =>
                      onFieldChange("max_discount_amount", parseFloat(e.target.value) || 0)
                    }
                    disabled={isInputDisabled}
                  />
                </div>
              </div>
            )}

            {/* Apply discount on (for Transaction + non-Rate) */}
            {formData.apply_on === "Transaction" && rateOrDiscount && rateOrDiscount !== "Rate" && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t("apply_on_label")}</Label>
                <Select
                  value={(formData.apply_discount_on as string) || "Grand Total"}
                  onValueChange={(val) => onFieldChange("apply_discount_on", val)}
                  disabled={isInputDisabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Grand Total">Grand Total</SelectItem>
                    <SelectItem value="Net Total">Net Total</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Price list (for non-Rate) */}
            {rateOrDiscount && rateOrDiscount !== "Rate" && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t("price_list")}</Label>
                <DocTypeSearchAutocomplete
                  doctype="Price List"
                  value={(formData.for_price_list as string) || ""}
                  onChange={(val) => onFieldChange("for_price_list", val)}
                  placeholder={t("price_list")}
                  disabled={isInputDisabled}
                  showInitialSuggestions
                  minSearchLength={0}
                />
              </div>
            )}
          </>
        )}

        {mode === "product" && (
          <>
            {/* Same item checkbox */}
            <div className="flex items-start gap-3">
              <Checkbox
                checked={!!formData.same_item}
                onCheckedChange={(checked) => onFieldChange("same_item", checked ? 1 : 0)}
                disabled={isInputDisabled}
                id="same_item"
              />
              <div>
                <Label htmlFor="same_item" className="text-sm font-medium cursor-pointer">
                  {t("same_item_label")}
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("same_item_hint")}
                </p>
              </div>
            </div>

            {/* Free item (shown when same_item is unchecked) */}
            {!formData.same_item && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t("free_item")}</Label>
                <DocTypeSearchAutocomplete
                  doctype="Item"
                  value={(formData.free_item as string) || ""}
                  onChange={(val) => onFieldChange("free_item", val)}
                  placeholder={t("select_item")}
                  disabled={isInputDisabled}
                  showInitialSuggestions
                  minSearchLength={0}
                />
              </div>
            )}

            {/* Free qty + rate */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t("free_qty")}</Label>
                <Input
                  type="number"
                  value={(formData.free_qty as number) ?? ""}
                  onChange={(e) => onFieldChange("free_qty", parseFloat(e.target.value) || 0)}
                  disabled={isInputDisabled}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t("free_rate")}</Label>
                <Input
                  type="number"
                  value={(formData.free_item_rate as number) ?? ""}
                  onChange={(e) =>
                    onFieldChange("free_item_rate", parseFloat(e.target.value) || 0)
                  }
                  placeholder="0"
                  disabled={isInputDisabled}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
